/**
 * AddDatasourceModal Component
 * Multi-step wizard for adding new datasources
 */

import React, { useState } from 'react';
import { Modal } from '../shared/Modal';
import { Stepper } from '../shared/Stepper';
import { TypeSelector } from './TypeSelector';
import { CredentialsForm } from './CredentialsForm';
import { TestConnection } from './TestConnection';
import { SyncSettings } from './SyncSettings';
import { datasourceService, CreateDatasourcePayload } from '../../services/datasources.service';

interface AddDatasourceModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

type DatasourceType = 'salesforce' | 'sap_erp' | 'odoo';

interface WizardData {
  type: DatasourceType | null;
  credentials: Record<string, any>;
  name: string;
  description: string;
  syncEnabled: boolean;
  syncFrequency: number;
  allowedRoles: string[];
}

const steps = [
  { name: 'Type', description: 'Select platform' },
  { name: 'Credentials', description: 'Enter details' },
  { name: 'Test', description: 'Verify connection' },
  { name: 'Configure', description: 'Sync settings' },
];

export const AddDatasourceModal: React.FC<AddDatasourceModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({
    type: null,
    credentials: {},
    name: '',
    description: '',
    syncEnabled: true,
    syncFrequency: 60,
    allowedRoles: [],
  });
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const handleTypeSelect = (type: DatasourceType) => {
    setWizardData({ ...wizardData, type });
    setCurrentStep(1);
  };

  const handleCredentialsSubmit = (credentials: Record<string, any>) => {
    setWizardData({ ...wizardData, credentials });
    setCurrentStep(2);
  };

  const handleTestSuccess = () => {
    setCurrentStep(3);
  };

  const handleFinalSubmit = async (settings: {
    name: string;
    description: string;
    syncEnabled: boolean;
    syncFrequency: number;
    allowedRoles: string[];
  }) => {
    if (!wizardData.type) return;

    try {
      setSaving(true);

      const payload: CreateDatasourcePayload = {
        type: wizardData.type,
        name: settings.name,
        description: settings.description,
        credentials: wizardData.credentials,
        allowedRoles: settings.allowedRoles,
        syncEnabled: settings.syncEnabled,
        syncFrequencyMinutes: settings.syncFrequency,
      };

      await datasourceService.createDatasource(payload);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create datasource:', error);
      alert(error.response?.data?.error?.message || 'Failed to create datasource');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Modal isOpen={true} size="xl" onClose={onClose} title="Connect a source">
      <div>
        {/* Stepper */}
        <div style={{ marginBottom: 22 }}>
          <Stepper steps={steps} currentStep={currentStep} />
        </div>

        {/* Step Content */}
        <div style={{ minHeight: 360 }}>
          {currentStep === 0 && (
            <TypeSelector onSelect={handleTypeSelect} />
          )}

          {currentStep === 1 && wizardData.type && (
            <CredentialsForm
              type={wizardData.type}
              initialValues={wizardData.credentials}
              onSubmit={handleCredentialsSubmit}
              onBack={handleBack}
            />
          )}

          {currentStep === 2 && wizardData.type && (
            <TestConnection
              type={wizardData.type}
              credentials={wizardData.credentials}
              onSuccess={handleTestSuccess}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <SyncSettings
              initialValues={{
                name: wizardData.name,
                description: wizardData.description,
                syncEnabled: wizardData.syncEnabled,
                syncFrequency: wizardData.syncFrequency,
                allowedRoles: wizardData.allowedRoles,
              }}
              onSubmit={handleFinalSubmit}
              onBack={handleBack}
              saving={saving}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
